import React from 'react'

/**
 * Context for passing tutorial panel highlight info down to panel sub-components.
 * Value shape: { changedKeys: string[], panelType: string } | null
 *
 * changedKeys: state keys that will change in the next tutorial step
 *   e.g., ['assignservo_motor1', 'valueservo_motor1'] for hardware
 *   e.g., ['ms', 's'] for wait
 *   e.g., ['source', 'condition', 'value'] for sensor/condition
 *
 * panelType: the type of the active hex block ('hardware', 'wait', 'sensor', etc.)
 */
const PanelHighlightContext = React.createContext(null)

/**
 * Helper: Check if a given port/key should be highlighted.
 * Works by checking if any changedKey contains the port identifier.
 *
 * @param {object|null} highlightInfo - Context value from PanelHighlightContext
 * @param {string} port - The port identifier (e.g., 'S1', 'servo_motor1', 'SmileOne')
 * @returns {boolean}
 */
export const isPortHighlighted = (highlightInfo, port) => {
    if (!highlightInfo || !highlightInfo.changedKeys || !port) return false
    return highlightInfo.changedKeys.some(
        (key) => key.toLowerCase().includes(port.toLowerCase())
    )
}

/**
 * Helper: Check if a specific state key is in the highlight list.
 *
 * @param {object|null} highlightInfo - Context value from PanelHighlightContext
 * @param {string} key - The exact state key (e.g., 'ms', 'h', 'source', 'condition')
 * @returns {boolean}
 */
export const isKeyHighlighted = (highlightInfo, key) => {
    if (!highlightInfo || !highlightInfo.changedKeys || !key) return false;
    
    // Exact match based on the 'hardwareKey' provided in the JSON subStep
    return highlightInfo.changedKeys.includes(key);
}

export const isConditionHighlighted = (highlightInfo, key, expectedValue) => {
    if (!highlightInfo || !highlightInfo.changedKeys || !key) return false;
    
    const isKeyMatch = highlightInfo.changedKeys.includes(key);
    
    // Ensure your context provider is passing the full tutorial step data 
    // so highlightInfo.expectedValue is accessible here.
    const isValueMatch = highlightInfo.expectedValue === expectedValue;
    
    return isKeyMatch && isValueMatch;
}

export const getUiTarget = (highlightInfo) => {
    // Optionally use this to add CSS glow to specific buttons by their string ID
    return highlightInfo ? highlightInfo.uiTarget : null; 
}

export default PanelHighlightContext
